import type { Writable } from "@glideapps/ts-necessities";
import entries from "lodash/entries";
import type { SerializedComponent } from "../../common/components/SerializedApp";
import { logError } from "../../common/debug-print";
import type {
    ActionDescription,
    ActionKind,
    Description,
    InputOutputTables,
    MutatingScreenKind,
    TableColumn,
} from "../../common/description";
import { assert, mapFilterUndefined, mapFilterUndefinedAsync } from "../../common/Support";
import type {
    ActionsRecord,
    AppDescriptionContext,
    ComponentBuilder,
    InteractiveComponentConfiguratorContext,
    RewritingComponentConfiguratorContext,
} from "../components";
import type { LetBinding } from "../serialized-component";
import type { ActionHandler } from "./action-handler";

const actionHandlers: Map<ActionKind, ActionHandler<ActionDescription>> = new Map();

export function registerActionHandler(handler: ActionHandler<ActionDescription>): void {
    const { kind } = handler;
    assert(!actionHandlers.has(kind));
    actionHandlers.set(kind, handler);
}

export function handlerForActionKind(kind: ActionKind): ActionHandler<ActionDescription> | undefined {
    const handler = actionHandlers.get(kind);
    if (handler === undefined) {
        logError("No handler for action kind", kind);
    }
    return handler;
}

export function actionsNeedScreenContext(
    descs: ReadonlyArray<ActionDescription>,
    tables: InputOutputTables,
    ccc: AppDescriptionContext,
    mutatingScreenKind: MutatingScreenKind | undefined
): boolean {
    return descs.some(desc => {
        const handler = handlerForActionKind(desc.kind);
        if (handler === undefined) return false;
        return handler.getDescriptor(desc, tables, ccc, mutatingScreenKind)?.needsScreenContext === true;
    });
}

export function makeScreenContextForActions(
    builder: ComponentBuilder,
    actions: readonly ActionDescription[],
    tables: InputOutputTables,
    mutatingScreenKind: MutatingScreenKind | undefined
): { screenContext: SerializedComponent | undefined; lets: LetBinding[] } {
    const { b } = builder;
    let screenContext: SerializedComponent | undefined;
    const lets: LetBinding[] = [];
    if (actionsNeedScreenContext(actions, tables, builder, mutatingScreenKind)) {
        screenContext = b.getVariable("screenContext");
        lets.push(["screenContext", b.getContext]);
    }
    return { screenContext, lets };
}

export async function copyActions(
    actions: readonly ActionDescription[],
    rootDesc: Description,
    copyTables: InputOutputTables | undefined,
    iccc: InteractiveComponentConfiguratorContext,
    mutatingScreenKind: MutatingScreenKind | undefined,
    screensCreated: Set<string>
): Promise<readonly ActionDescription[]> {
    return await mapFilterUndefinedAsync(
        actions,
        async a =>
            await handlerForActionKind(a.kind)?.duplicateAction(
                a,
                rootDesc,
                copyTables,
                iccc,
                mutatingScreenKind,
                screensCreated
            )
    );
}

export async function copyActionsRecord(
    actionsRecord: ActionsRecord,
    rootDesc: Description,
    copyTables: InputOutputTables | undefined,
    iccc: InteractiveComponentConfiguratorContext,
    mutatingScreenKind: MutatingScreenKind | undefined,
    screensCreated: Set<string>
): Promise<ActionsRecord> {
    const copy: Writable<ActionsRecord> = { ...actionsRecord };
    for (const [name, actions] of entries(copy)) {
        copy[name] = await copyActions(actions, rootDesc, copyTables, iccc, mutatingScreenKind, screensCreated);
    }
    return copy;
}

export function defaultActionForColumn(column: TableColumn): ActionDescription | undefined {
    for (const handler of actionHandlers.values()) {
        const desc = handler.newDefaultActionDescription(column);
        if (desc !== undefined) {
            return desc;
        }
    }
    return undefined;
}

export function rewriteActions(
    actions: readonly ActionDescription[] | undefined,
    tables: InputOutputTables,
    ccc: RewritingComponentConfiguratorContext,
    mutatingScreenKind: MutatingScreenKind | undefined
): readonly ActionDescription[] | undefined {
    if (actions === undefined) return undefined;
    return mapFilterUndefined(actions, action => {
        const handler = handlerForActionKind(action.kind);
        if (handler === undefined) return undefined;
        return handler.rewriteAfterReload(action, tables, ccc, mutatingScreenKind);
    });
}
